/**
 * Theme Constants
 * Single source of truth for light and dark mode color palettes
 */

export type ThemeMode = "light" | "dark";

export interface ThemeColors {
  // Background colors
  background: {
    primary: string;
    secondary: string;
    tertiary: string;
    panel: string;
    card: string;
  };

  // Text colors
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    muted: string;
    inverse: string;
  };

  // Border colors
  border: { primary: string; secondary: string; focus: string };

  // Brand colors (consistent across themes)
  brand: { primary: string; secondary: string; accent: string };

  // Status colors (consistent across themes)
  status: { success: string; warning: string; danger: string; info: string };

  // Component-specific colors
  component: { sidebar: string; nav: string; glass: string; shadow: string };
}

export const DARK_THEME: ThemeColors = {
  background: {
    card: "#1f2937",
    panel: "rgba(17, 24, 39, 0.78)",
    primary: "#0f1729",
    secondary: "#1f2937",
    tertiary: "#374151",
  },

  border: { focus: "#4c7ff0", primary: "#374151", secondary: "#4b5563" },

  brand: { accent: "#2dd4ff", primary: "#4c7ff0", secondary: "#38bdf8" },

  component: {
    glass: "rgba(17, 24, 39, 0.4)",
    nav: "rgba(31, 41, 55, 0.8)",
    shadow: "rgba(0, 0, 0, 0.3)",
    sidebar: "rgba(15, 23, 41, 0.95)",
  },

  status: {
    danger: "#ef4444",
    info: "#3b82f6",
    success: "#22c55e",
    warning: "#facc15",
  },

  text: {
    inverse: "#111827",
    muted: "#9ca3af",
    primary: "#f9fafb",
    secondary: "#e5e7eb",
    tertiary: "#d1d5db",
  },
};

export const LIGHT_THEME: ThemeColors = {
  background: {
    card: "#ffffff",
    panel: "rgba(255, 255, 255, 0.95)",
    primary: "#ffffff",
    secondary: "#f9fafb",
    tertiary: "#f3f4f6",
  },

  border: { focus: "#4c7ff0", primary: "#e5e7eb", secondary: "#d1d5db" },

  brand: { accent: "#2dd4ff", primary: "#4c7ff0", secondary: "#38bdf8" },

  component: {
    glass: "rgba(255, 255, 255, 0.6)",
    nav: "rgba(255, 255, 255, 0.9)",
    shadow: "rgba(0, 0, 0, 0.1)",
    sidebar: "rgba(249, 250, 251, 0.95)",
  },

  status: {
    danger: "#ef4444",
    info: "#3b82f6",
    success: "#22c55e",
    warning: "#f59e0b",
  },

  text: {
    inverse: "#ffffff",
    muted: "#9ca3af",
    primary: "#111827",
    secondary: "#374151",
    tertiary: "#6b7280",
  },
};

/**
 * Get theme colors based on current theme mode
 */
export function getThemeColors(mode: ThemeMode): ThemeColors {
  return mode === "dark" ? DARK_THEME : LIGHT_THEME;
}

/**
 * Local storage key for theme persistence
 */
export const THEME_STORAGE_KEY = "arti-dashboard-theme";

/**
 * Default theme mode
 */
export const DEFAULT_THEME: ThemeMode = "dark";
